// notify/index.js

import { Notification, Color } from './notification';
import debounce from 'lodash/debounce';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import React from 'react';
import History from 'core/utils/history';

const isExternalUrl = (url?: string | null) => /^https?:\/\//i.test(url || '');

const openActionUrl = (url?: string | null) => {
    if (!url) {
        return;
    }

    if (isExternalUrl(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
    }

    if (url.startsWith('/')) {
        History.navigate(url);
    }
};

const ModernToast = ({ title, message, type, actionUrl }: any) => {
    return (
        <div className="flex flex-col gap-1 max-w-[350px] break-words">
            <span
                className={`font-bold text-gray-900 text-[2vh] capitalize ${actionUrl ? 'cursor-pointer hover:underline' : ''}`}
                onClick={() => openActionUrl(actionUrl)}
            >
                {title}
            </span>
            <span className="text-gray-600 text-[1.65vh] leading-relaxed">
                {message}
            </span>
            {actionUrl && (
                <button
                    type="button"
                    className="w-fit text-primary-main text-[1.45vh] font-semibold hover:underline"
                    onClick={() => openActionUrl(actionUrl)}
                >
                    Open update
                </button>
            )}
        </div>
    );
};

const getToastOptions = (type: string) => {
    let borderColor = '';
    let icon: React.ReactNode = null;
    let bgColor = '#ffffffcc'; // Default semi-transparent white
    
    switch(type) {
        case 'success':
            borderColor = '#86efac';
            bgColor = '#f0fdf4cc'; // 80% opacity
            icon = <div className="bg-green-100 p-1.5 rounded-full"><div className="w-2 h-2 bg-green-500 rounded-full" /></div>;
            break;
        case 'warning':
            borderColor = '#fcd34d'; 
            bgColor = '#fffbebcc'; 
            icon = <div className="bg-amber-100 p-1.5 rounded-full"><div className="w-2 h-2 bg-amber-500 rounded-full" /></div>;
            break;
        case 'error':
            borderColor = '#fca5a5';
            bgColor = '#fef2f2cc';
            icon = <div className="bg-red-100 p-1.5 rounded-full"><div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /></div>;
            break;
        case 'info':
        default:
            borderColor = '#93c5fd';
            bgColor = '#eff6ffcc';
            icon = <div className="bg-blue-100 p-1.5 rounded-full"><div className="w-2 h-2 bg-blue-500 rounded-full" /></div>;
            break;
    }

    return {
        position: "top-center" as const,
        autoClose: 3000, // Reduced from default 5000ms to 3000ms
        hideProgressBar: false,
        style: {
            background: bgColor,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)', // Safari support
            color: '#1f2937',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${borderColor}`,
            padding: '0.8vh 1.5vh', 
        },
        icon: icon,
    };
};

const showModernToast = (message: any, type: string, title?: string, actionUrl?: string | null, toastId?: string) => {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/digital-marketing')) {
        return;
    }

    toast(
        <ModernToast message={message} type={type} title={title || type} actionUrl={actionUrl} />,
        {
            ...getToastOptions(type),
            toastId,
            ...(actionUrl ? { closeOnClick: false } : {}),
        },
    );
}

// --- Rich foreground push notification ----------------------------------------
// Rendered when an FCM message arrives while the app is in the foreground. Unlike
// the generic info/success toasts, this shows the app icon (or a hero image),
// title + body, and navigates to the notification's deep link on click.
const DEFAULT_PUSH_ICON = '/logo192.webp';

const PushNotificationToast = ({ title, body, icon, image }: any) => {
    return (
        <div className="flex gap-3 max-w-[360px] cursor-pointer">
            <img
                src={icon || DEFAULT_PUSH_ICON}
                alt=""
                className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                onError={(e: any) => { e.currentTarget.src = DEFAULT_PUSH_ICON; }}
            />
            <div className="flex flex-col gap-1 min-w-0">
                <span className="font-bold text-gray-900 text-[1.9vh] break-words">{title}</span>
                {body ? (
                    <span className="text-gray-600 text-[1.6vh] leading-relaxed break-words line-clamp-3">
                        {body}
                    </span>
                ) : null}
                {image ? (
                    <img
                        src={image}
                        alt=""
                        className="mt-1 w-full max-h-[140px] rounded-lg object-cover"
                        onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                    />
                ) : null}
            </div>
        </div>
    );
};

/**
 * Show a rich foreground push notification toast.
 * `payload` accepts: title, body, icon, image, url (deep link opened on click).
 */
export function pushNotify(payload: {
    title: string;
    body?: string;
    icon?: string;
    image?: string;
    url?: string;
}) {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/digital-marketing')) {
        return;
    }
    const { title, body, icon, image, url } = payload || ({} as any);
    if (!title) {
        return;
    }

    toast(
        <PushNotificationToast title={title} body={body} icon={icon} image={image} />,
        {
            ...getToastOptions('info'),
            autoClose: 6000,
            onClick: () => {
                if (url) {
                    if (url.startsWith('http')) {
                        window.open(url, '_blank', 'noopener');
                    } else {
                        window.location.assign(url);
                    }
                }
            },
        }
    );
}

const debouncedToast = debounce((message,type) => {
  if (Array.isArray(message)) {
    message.forEach((msg) => {
      showModernToast(msg, type);
    });
  } else {
    showModernToast(message, type);
  }
}, 300); // 300ms debounce delay

export { Notification, Color };

export function info(children, autoClose) {
  return debouncedToast(children, "info");
}

export function notificationInfo({ title, message, actionUrl, toastId }: { title: string; message?: string; actionUrl?: string | null; toastId?: string }) {
  return showModernToast(message || '', "info", title, actionUrl, toastId);
}

export function success(children, autoClose) {
  return debouncedToast(children, "success");
}

export function warning(children, autoClose) {
  return debouncedToast(children, "warning");
}

export function error(children, autoClose) {
  return debouncedToast(children, "error");
}
